// Dependencies
import React from 'react';

import classnames from 'classnames';
import Lazy from 'react-lazyload';

// Utils
import { Button, Text, Box } from '@nerdwallet/currency';
import { Num } from '@nerdwallet/nw-client-lib';
import { cdnUri, iconUri } from '@nerdwallet/nw-client-lib/cdn';

// Local components
import ImageSizes from '../../../config/images/imageSizes';
import AuthModal from '../../../containers/auth-modal';
import {
  getEventType,
  getEventDetails,
  getEventEntityName,
  getJoinNerdwallet,
} from '../../../lib/analytics-constants';

// CSS
import style from './credit-score-cta.module.less';

export interface CreditScoreCtaProps {
  creditScore?: number;
  cta?: string;
  description: string;
  icon?: string;
  flow?: string;
  flowConfig?: Record<string, any>;
  header?: string;
  interestRate: number;
  isSignedIn?: boolean;
  mobileCta?: boolean;
  tracking?: string;
}

export const CreditScoreCta: React.FC<CreditScoreCtaProps> = (
  props: CreditScoreCtaProps
) => {
  const {
    creditScore,
    cta,
    description,
    icon,
    flow,
    flowConfig,
    header,
    interestRate,
    isSignedIn,
    mobileCta,
    tracking,
  } = props;

  const renderResults = () => {
    return (
      <div className={style.creditScoreCtaOutput}>
        <div className={style.creditScoreCtaOutputWrapper}>
          <div className={style.creditScoreCtaOutputText}>
            Your credit score:
          </div>

          <div
            className={classnames(style.creditScoreCtaOutputValue, {
              hide: !creditScore,
            })}
          >
            {creditScore || '???'}
          </div>

          <img
            className={classnames(style.creditScoreCtaOutputPlaceholder, {
              hide: !!creditScore,
            })}
            src={cdnUri('img/diy/hac', 'png')('CreditScoreGauge')}
            alt=""
          />
        </div>

        <div className={style.creditScoreCtaOutputWrapper}>
          <div className={style.creditScoreCtaOutputText}>
            Your likely rate:
          </div>
          <div className={style.creditScoreCtaOutputValue}>
            {creditScore ? Num.formatPercent(interestRate) : '?%'}
          </div>
        </div>
      </div>
    );
  };

  const renderButtonCta = () => {
    if (creditScore) return null;

    const ctaText =
      cta || (isSignedIn ? 'Check your credit score' : 'Join NerdWallet');

    const getJoinNerdwalletEvent = getJoinNerdwallet();
    const getJoinNerdwalletDetails = getEventDetails(getJoinNerdwalletEvent);

    return (
      <AuthModal
        driverLocation={tracking}
        flow={flow}
        flowConfig={flowConfig}
        tracking={tracking}
        impressionPayload={{
          eventType: getEventType(getJoinNerdwalletEvent),
          entityName: getEventEntityName(getJoinNerdwalletEvent),
          payload: getJoinNerdwalletDetails,
        }}
      >
        <Button primary className={style.creditScoreCtaButton}>
          {ctaText}
        </Button>
      </AuthModal>
    );
  };

  const renderDesktopCta = () => {
    // Signed in & credit score
    if (creditScore) {
      return renderResults();
    }

    // Signed in & no credit score
    if (isSignedIn) {
      return (
        <div>
          <div
            key="credit-score-cta-header"
            className={style.creditScoreCtaHeader}
          >
            <Lazy>
              <img
                className={style.creditScoreCtaHeaderImg}
                {...ImageSizes.largeIcon.measures}
                src={iconUri(icon || 'Credit_score')}
                alt=""
              />
            </Lazy>
            <div className={style.creditScoreCtaHeaderText}>
              Welcome to NerdWallet!
            </div>
          </div>

          <Box paddingTop="1">
            <Text size="small" key="credit-score-cta-description">
              {description}
            </Text>
          </Box>

          {renderButtonCta()}
        </div>
      );
    }

    // Signed out

    return (
      <div>
        <div
          key="credit-score-cta-header"
          className={style.creditScoreCtaHeader}
        >
          <Lazy>
            <img
              className={style.creditScoreCtaHeaderImg}
              {...ImageSizes.largeIcon.measures}
              src={iconUri(icon || 'Credit_score')}
              alt=""
            />
          </Lazy>
          <Text bold className={style.creditScoreCtaHeaderText}>
            {header}
          </Text>
        </div>

        <Box paddingTop="1">
          <Text size="small" key="credit-score-cta-description">
            {description}
          </Text>
        </Box>

        {renderButtonCta()}
      </div>
    );
  };

  const renderMobileCta = () => {
    if (creditScore) {
      return (
        <div className={style.creditScoreCtaOutput}>
          <div className={style.creditScoreCtaOutputWrapper}>
            <div className={style.creditScoreCtaOutputText}>
              Your credit score:
            </div>
            <div className={style.creditScoreCtaOutputValue}>{creditScore}</div>
          </div>

          <div className={style.creditScoreCtaOutputWrapper}>
            <div className={style.creditScoreCtaOutputText}>
              Your likely rate:
            </div>
            <div className={style.creditScoreCtaOutputValue}>
              {Num.formatPercent(interestRate)}
            </div>
          </div>
        </div>
      );
    }

    const getJoinNerdwalletEvent = getJoinNerdwallet();
    const getJoinNerdwalletDetails = getEventDetails(getJoinNerdwalletEvent);
    return (
      <AuthModal
        driverLocation={tracking}
        tracking={tracking}
        impressionPayload={{
          eventType: getEventType(getJoinNerdwalletEvent),
          entityName: getEventEntityName(getJoinNerdwalletEvent),
          payload: getJoinNerdwalletDetails,
        }}
      >
        <div
          key="credit-score-cta-header"
          className={style.creditScoreCtaHeader}
        >
          <Lazy>
            <img
              className={style.creditScoreCtaHeaderImg}
              {...ImageSizes.largeIcon.measures}
              src={iconUri('Credit_score')}
              alt=""
            />
          </Lazy>
          <div className={style.creditScoreCtaHeaderText}>
            What rate will you likely qualify for?{' '}
            <a>Get your free credit score.</a>
          </div>
        </div>
      </AuthModal>
    );
  };

  return (
    <div
      className={classnames(
        `${style.creditScoreCta} ${style.creditScoreCta}${
          mobileCta ? 'Mobile' : 'Desktop'
        }`,
        { 'credit-score': creditScore }
      )}
    >
      {mobileCta ? renderMobileCta() : renderDesktopCta()}
    </div>
  );
};

CreditScoreCta.displayName = 'CreditScoreCta';

CreditScoreCta.defaultProps = {
  creditScore: null,
  cta: null,
  icon: null,
  flow: null,
  flowConfig: null,
  header: null,
  isSignedIn: false,
  mobileCta: false,
  tracking: null,
};

export default CreditScoreCta;
